
import { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { SmartTopNav } from './SmartTopNav';
import { BottomNav } from './BottomNav';
import { SwipeablePageWrapper } from '@/components/navigation/SwipeablePageWrapper';

import { OfflineIndicator } from '@/components/ui/offline-indicator';

interface MobileLayoutProps {
  children: ReactNode;
}

export function MobileLayout({ children }: MobileLayoutProps) {
  const location = useLocation();

  // Define routes where bottom navigation should be hidden
  const hideBottomNavRoutes = [
    '/play/solo/',
    '/play/multiplayer/',
    '/tournament/',
  ];

  // Routes where the bottom nav is always hidden (full-screen immersive pages)
  const hideBottomNavAlways = ['/club/chat', '/community/chat', '/practice/', '/create-session'];

  // Check if current route should hide bottom nav (during active gameplay)
  const shouldHideBottomNav =
    hideBottomNavAlways.some(route => location.pathname.startsWith(route)) ||
    hideBottomNavRoutes.some(route =>
      location.pathname.includes(route) &&
      (location.pathname.includes('/score') || location.pathname.includes('/lobby'))
    );

  // Check if we should show the top nav
  // Hide on auth, landing, and during active score entry (GPS map needs full screen)
  // Note: shouldHideBottomNav covers gameplay routes only — chat hides bottom nav but keeps top nav
  // Screens pushed from a tab render their own PageBackHeader (back + title).
  const isPushedWithOwnHeader =
    location.pathname.startsWith('/practice/') ||
    location.pathname === '/profile/settings';
  const hideTopNav = location.pathname.includes('/auth') ||
    location.pathname === '/' ||
    isPushedWithOwnHeader ||
    location.pathname.startsWith('/club/chat') ||
    location.pathname.startsWith('/community/chat') ||
    hideBottomNavRoutes.some(route =>
      location.pathname.includes(route) &&
      (location.pathname.includes('/score') || location.pathname.includes('/lobby'))
    );
  const showTopNav = !hideTopNav;

  return (
    <div className="min-h-screen flex flex-col w-full bg-app overflow-x-hidden">
      {showTopNav ? <SmartTopNav /> : null}

      <main
        className={`flex-1 overflow-x-hidden`}
        style={{
          ...(showTopNav ? { paddingTop: 'calc(60px + env(safe-area-inset-top, 0px))' } : {}),
          ...(!shouldHideBottomNav ? { paddingBottom: 'calc(80px + env(safe-area-inset-bottom, 0px))' } : {}),
        }}
      >
        <SwipeablePageWrapper>
          {children}
        </SwipeablePageWrapper>
      </main>

      {!shouldHideBottomNav && <BottomNav />}

    </div>
  );
}
